import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Grid,
  Button,
  FormControl,
  Input,
  InputLabel,
  Skeleton,
  Paper,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Badge,
  Tooltip
} from "@mui/material";
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import axios from "axios";
import { API_URL, popupTimer } from "../../consts";
import { useDispatch, useSelector } from "react-redux";
import store from "../../Redux/store";
import ConfirmationDialog from "../common/ConfirmationDialog";
import TopicFilter from "../common/TopicFilter";
import HomeCardItem from "./HomeCardItem";
import FeedCardItem from "./FeedCardItem";
import {  } from "react-redux";
import { useNavigate } from "react-router-dom";
import MobileFilterView from "../common/MobileFilterView";

const CommunityRates = ({ organizationMode = false, organizationId = null, pageTitle = "Explore community" }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [topTopics, setTopTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [filterBy, setFilterBy] = useState(["You may love.."]);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removedRateId, setRemovedRateId] = useState(null);
  
  // Add pagination states
  const [ratesPage, setRatesPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreRates, setHasMoreRates] = useState(true);
  const containerRef = useRef(null);
  
  // Add view mode state - default to 'feed' on mobile, 'grid' on desktop
  const [viewMode, setViewMode] = useState(isMobile ? 'feed' : 'grid');
  const previousViewModeRef = useRef(null); // Store previous view mode when chat opens
  const previousViewModeForResizeRef = useRef(null); // Store previous view mode when resize forces feed
  
  // Add states for filter visibility
  const [showTopicFilter, setShowTopicFilter] = useState(false);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [mobileTopicsData, setMobileTopicsData] = useState([]);
  const [showMobileTopicModal, setShowMobileTopicModal] = useState(false);

  // Get chat open state from Redux
  const chatOpen = useSelector((state) => state.chatOpen);

  // Update view mode when screen size changes
  useEffect(() => {
    setViewMode(isMobile ? 'feed' : 'grid');
  }, [isMobile]);

  // Handle chat open/close - switch to feed view when chat opens, restore previous when closes
  useEffect(() => {
    if (chatOpen) {
      // Chat opened - save current view mode if not already feed, then switch to feed
      if (viewMode !== 'feed') {
        previousViewModeRef.current = viewMode;
        setViewMode('feed');
      }
    } else {
      // Chat closed - restore previous view mode if it wasn't already feed
      // But only if card width allows it (checked by resize observer)
      if (previousViewModeRef.current && previousViewModeRef.current !== 'feed') {
        // Check if we can restore (card width >= 200px)
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const padding = 32;
          const spacing = 24;
          let cardWidth;
          if (containerWidth >= 960) {
            cardWidth = (containerWidth - padding - spacing * 2) / 3;
          } else if (containerWidth >= 600) {
            cardWidth = (containerWidth - padding - spacing) / 2;
          } else {
            cardWidth = containerWidth - padding;
          }
          
          if (cardWidth >= 200) {
            setViewMode(previousViewModeRef.current);
            previousViewModeRef.current = null;
          }
        }
      }
    }
  }, [chatOpen, viewMode]);

  // Handle resize - switch to feed view when card width < 200px
  useEffect(() => {
    if (!containerRef.current || viewMode === 'feed') return;

    const calculateCardWidth = () => {
      const container = containerRef.current;
      if (!container) return null;

      const containerWidth = container.clientWidth;
      const padding = 32; // Approximate padding (16px on each side)
      const spacing = 24; // Grid spacing

      // Determine breakpoint based on container width
      let cardWidth;
      if (containerWidth >= 960) {
        // md breakpoint: 3 columns
        cardWidth = (containerWidth - padding - spacing * 2) / 3;
      } else if (containerWidth >= 600) {
        // sm breakpoint: 2 columns
        cardWidth = (containerWidth - padding - spacing) / 2;
      } else {
        // xs breakpoint: 1 column
        cardWidth = containerWidth - padding;
      }

      return cardWidth;
    };

    const checkCardWidth = () => {
      const cardWidth = calculateCardWidth();
      if (cardWidth === null) return;

      if (cardWidth < 200 && viewMode !== 'feed') {
        // Card too small - switch to feed view
        if (!previousViewModeForResizeRef.current) {
          previousViewModeForResizeRef.current = viewMode;
        }
        setViewMode('feed');
      } else if (cardWidth >= 200 && previousViewModeForResizeRef.current) {
        // Card width restored - restore previous view mode
        setViewMode(previousViewModeForResizeRef.current);
        previousViewModeForResizeRef.current = null;
      }
    };

    // Check on mount and resize
    checkCardWidth();

    const resizeObserver = new ResizeObserver(() => {
      checkCardWidth();
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [viewMode]);

  // Header visibility control for all views
  const headerVisibleTimeoutRef = useRef(null);
  const lastScrollY = useRef(0);
  const isScrolling = useRef(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  
  useEffect(() => {
    // Always show initially
    setHeaderVisible(true);
    
    // Handle scroll for both feed and grid views
    const handleScroll = () => {
      let currentScrollY;
      
      if (viewMode === 'feed') {
        // For feed view, use window scroll
        currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      } else {
        // For grid view, use container scroll
        if (!containerRef.current) return;
        currentScrollY = containerRef.current.scrollTop;
      }
      
      // Always show header at the top
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        isScrolling.current = false;
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        lastScrollY.current = currentScrollY;
        return;
      }
      
      // Only hide/show on actual scroll movement (ignore if scroll position hasn't changed)
      if (Math.abs(currentScrollY - lastScrollY.current) < 3) {
        return;
      }
      
      const scrollDirection = currentScrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = currentScrollY;
      
      // Show header immediately on scroll up
      if (scrollDirection === 'up') {
        isScrolling.current = false;
        setHeaderVisible(true);
        
        // Clear existing timeout
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
      } else {
        // Scroll down - hide header immediately
        isScrolling.current = true;
        setHeaderVisible(false);
        
        // Clear existing timeout before setting new one
        if (headerVisibleTimeoutRef.current) {
          clearTimeout(headerVisibleTimeoutRef.current);
          headerVisibleTimeoutRef.current = null;
        }
        
        // Show header after half a second of no scrolling
        headerVisibleTimeoutRef.current = setTimeout(() => {
          // Only show if we're still not scrolling
          if (isScrolling.current) {
            setHeaderVisible(true);
            isScrolling.current = false;
          }
          headerVisibleTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', throttledHandleScroll, { passive: true });
      }
    }
    
    return () => {
      if (viewMode === 'feed') {
        window.removeEventListener('scroll', throttledHandleScroll);
      } else {
        const container = containerRef.current;
        if (container) {
          container.removeEventListener('scroll', throttledHandleScroll);
        }
      }
      if (headerVisibleTimeoutRef.current) {
        clearTimeout(headerVisibleTimeoutRef.current);
        headerVisibleTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      isScrolling.current = false;
      setHeaderVisible(true);
    };
  }, [viewMode]);

  // Bottom nav visibility control for feed view on mobile only
  const bottomNavScrollStopTimeoutRef = useRef(null);
  const bottomNavLastScrollY = useRef(0);
  
  useEffect(() => {
    // Always show initially
    dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    bottomNavLastScrollY.current = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    
    if (!isMobile || viewMode !== 'feed') {
      // Reset to visible when not in feed view on mobile
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      return;
    }

    // For feed view on mobile, listen to window scroll for bottom nav
    const handleBottomNavScroll = () => {
      const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      
      // Always show nav at the top
      if (currentScrollY < 10) {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
        if (bottomNavScrollStopTimeoutRef.current) {
          clearTimeout(bottomNavScrollStopTimeoutRef.current);
          bottomNavScrollStopTimeoutRef.current = null;
        }
        bottomNavLastScrollY.current = currentScrollY;
        return;
      }
      
      // Only hide/show on actual scroll movement (ignore if scroll position hasn't changed)
      if (Math.abs(currentScrollY - bottomNavLastScrollY.current) < 3) {
        return;
      }
      
      const scrollDirection = currentScrollY > bottomNavLastScrollY.current ? 'down' : 'up';
      bottomNavLastScrollY.current = currentScrollY;
      
      // Clear any existing timeout when scrolling
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      
      // Show bottom nav immediately on scroll up
      if (scrollDirection === 'up') {
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
      } else {
        // Scroll down - hide bottom nav immediately
        dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: false });
        
        // Set timeout to show nav after scrolling stops (500ms of no scrolling)
        bottomNavScrollStopTimeoutRef.current = setTimeout(() => {
          dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
          bottomNavScrollStopTimeoutRef.current = null;
        }, 500);
      }
    };

    // Use throttling for better performance
    let ticking = false;
    const throttledHandleBottomNavScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleBottomNavScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleBottomNavScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleBottomNavScroll);
      if (bottomNavScrollStopTimeoutRef.current) {
        clearTimeout(bottomNavScrollStopTimeoutRef.current);
        bottomNavScrollStopTimeoutRef.current = null;
      }
      // Reset to visible when component unmounts or view changes
      dispatch({ type: "SET_BOTTOM_NAV_VISIBLE", payload: true });
    };
  }, [isMobile, viewMode, dispatch]);
  
  // Get search state from Redux
  const searchState = useSelector((state) => state.search);
  const token = useSelector((state) => state.token);
  const setTokenInProcess = useSelector((state) => state.setTokenInProcess);
  const filterRates = useSelector((state) => state.filterRates);

  let navigate = useNavigate();

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("yourItemKey");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (filterRates && filterRates !== null) {
      setData(filterRates);
    }
  }, [filterRates]);

  // Update selected topics in Redux when they change
  useEffect(() => {
    dispatch({
      type: "UPDATE_SEARCH",
      payload: { selectedTopics }
    });
  }, [selectedTopics, dispatch]);

  const deleteRate = () => {
    let url = API_URL + `/rates/${removedRateId}/delete`;
    axios
      .post(url, null)
      .then(() => {
        getRatesRequest(filterBy, selectedTopics);
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
        dispatch({
          type: "SET_POPUP",
          payload: { text: "Rate removed successfully", type: "success" },
        });
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to remove rate. Please try again.",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  const getRatesRequest = useCallback((searchType, topics, page = 0, append = false) => {
    // CRITICAL: Check authorization state before making request to prevent loops
    const state = store.getState();
    if (!state.authorized && token) {
      // If we have a token but not authorized, don't make request (auth is failing)
      console.warn('Skipping request - authentication failed');
      return;
    }
    
    setIsLoading(!append);
    if (!append) setLoadError(false);
    let baseUrl = `/rates`;
    if (token) {
      baseUrl = `/rates/authenticated`;
    }
    let url = API_URL + baseUrl;
    let paramsBody = {
      tags: searchState.tags.length > 0 ? searchState.tags.join(',') : null,
      topics: topics.length > 0 ? topics.join(",") : null,
      start_from: page * 10,
      size: 10
    };
    
    // Add organization_id and share_with_organization for Organization Reviews (items shared with org)
    if (organizationMode && organizationId) {
      paramsBody.organization_id = organizationId;
      paramsBody.share_with_organization = true;
    }
    
    axios
      .get(url, { params: paramsBody })
      .then((res) => {
        const rates = res.data.rates || [];
        if (append) {
          setData(prev => [...prev, ...rates]);
        } else {
          setData(rates);
          setTopTopics(res.data.top_topics);
        }
        // If we got less than 10 items, there are no more
        setHasMoreRates(rates.length === 10);
        setLoadError(false);
        setIsLoadingMore(false);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoadingMore(false);
        setIsLoading(false);
        setHasMoreRates(false);
        if (err.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          // Don't retry - let interceptor handle it
          return;
        }
        // Non-401 failure on a fresh load: surface an error state (with retry),
        // distinct from the genuinely-empty state.
        if (!append) setLoadError(true);
      });
  }, [searchState.tags, token, dispatch, organizationMode, organizationId]);

  // Handle scroll event - works for both feed view (window scroll) and grid view (container scroll)
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMoreRates) return;

    // For feed view, use window scroll; for grid view, use container scroll
    let scrollTop, scrollHeight, clientHeight;
    
    if (viewMode === 'feed') {
      scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight || document.documentElement.clientHeight;
    } else {
      if (!containerRef.current) return;
      const container = containerRef.current;
      scrollTop = container.scrollTop;
      scrollHeight = container.scrollHeight;
      clientHeight = container.clientHeight;
    }

    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

    if (isNearBottom && hasMoreRates && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = ratesPage + 1;
      setRatesPage(nextPage);
      getRatesRequest(filterBy, selectedTopics, nextPage, true);
    }
  }, [ratesPage, isLoadingMore, hasMoreRates, filterBy, selectedTopics, getRatesRequest, viewMode]);

  // Add scroll event listener - window for feed view, container for grid view
  useEffect(() => {
    if (viewMode === 'feed') {
      // For feed view, listen to window scroll
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // For grid view, listen to container scroll
      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }
  }, [handleScroll, viewMode]);

  // Reset pagination when filters change
  useEffect(() => {
    setRatesPage(0);
    setHasMoreRates(true);
    setIsLoadingMore(false);
  }, [filterBy, selectedTopics, searchState.tags]);

  // Update active filters count
  useEffect(() => {
    const count = selectedTopics.length;
    setActiveFiltersCount(count);
  }, [selectedTopics]);

  // Open mobile modal when filter is shown on mobile
  useEffect(() => {
    if (isMobile && showTopicFilter && mobileTopicsData.length > 0) {
      const timer = setTimeout(() => {
        setShowMobileTopicModal(true);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setShowMobileTopicModal(false);
    }
  }, [isMobile, showTopicFilter, mobileTopicsData]);

  const handleTopicsReady = (topics) => {
    setMobileTopicsData(topics);
  };

  const handleMobileTopicApply = (selectedNames) => {
    setFilterBy(selectedNames.length > 0 ? selectedNames : ["You may love.."]);
    setSelectedTopics(selectedNames);
    if (selectedNames.length > 0) {
      getRatesRequest(selectedNames, selectedNames);
    }
  };

  useEffect(() => {
    if (!setTokenInProcess) {
      getRatesRequest(filterBy, selectedTopics);
    }
  }, [filterBy, selectedTopics, setTokenInProcess, searchState.tags, getRatesRequest, organizationMode, organizationId]);

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  return (
    <>
      <Box sx={{ 
        minHeight: '100vh',
        background: viewMode === 'feed' ? '#fafafa' : 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
        pt: viewMode === 'feed' ? { xs: 0, sm: 0 } : { xs: 1, sm: 4, md: 6 },
        px: viewMode === 'feed' ? 0 : { xs: viewMode === 'feed' ? 0.5 : 1, sm: 3, md: 4 },
        pb: viewMode === 'feed' ? { xs: 8, sm: 4 } : 0
      }}>
      <Box sx={{ maxWidth: viewMode === 'feed' ? '100%' : '1400px', mx: 'auto' }}>
        <Box sx={{ 
          mb: viewMode === 'feed' ? 0 : { xs: 1, sm: 2 },
          position: viewMode === 'feed' ? 'sticky' : 'relative',
          top: 0,
          zIndex: 10,
          background: viewMode === 'feed' ? '#fff' : 'transparent',
          borderBottom: viewMode === 'feed' ? '1px solid #efefef' : 'none',
          pb: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : 0,
          transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 1 },
            px: viewMode === 'feed' ? { xs: 1, sm: 2 } : { xs: 0.5, sm: 1 },
            py: viewMode === 'feed' ? { xs: 1, sm: 1.5 } : { xs: 0.25, sm: 0.5 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '100%',
            mx: viewMode === 'feed' ? 'auto' : 0
          }}>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                fontWeight: viewMode === 'feed' ? 600 : 700,
                fontSize: viewMode === 'feed' ? { xs: '1.1rem', sm: '1.25rem' } : { xs: '1rem', sm: '1.25rem', md: '1.4rem' },
                background: viewMode === 'feed' ? 'none' : 'linear-gradient(135deg, #1a202c 0%, #6366f1 100%)',
                color: viewMode === 'feed' ? '#262626' : 'transparent',
                WebkitBackgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                WebkitTextFillColor: viewMode === 'feed' ? '#262626' : 'transparent',
                backgroundClip: viewMode === 'feed' ? 'unset' : 'text',
                mb: 0
              }}
            >
              {pageTitle}
            </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {/* View Mode Toggle - Hidden on mobile */}
            {!isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{
                  background: viewMode === 'feed' ? 'transparent' : 'rgba(255,255,255,0.8)',
                  borderRadius: '8px',
                  border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                  boxShadow: viewMode === 'feed' ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
                  '& .MuiToggleButton-root': {
                    border: 'none',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.75,
                    color: viewMode === 'feed' ? '#8e8e8e' : 'rgba(71,85,105,0.8)',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: viewMode === 'feed' ? 'rgba(0,0,0,0.05)' : 'rgba(99,102,241,0.1)',
                      color: viewMode === 'feed' ? '#262626' : 'rgba(99,102,241,0.8)',
                    },
                    '&.Mui-selected': {
                      background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: viewMode === 'feed' ? '#262626' : 'white',
                      boxShadow: viewMode === 'feed' ? 'none' : '0 4px 12px rgba(99,102,241,0.3)',
                      '&:hover': {
                        background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                        boxShadow: viewMode === 'feed' ? 'none' : '0 6px 16px rgba(99,102,241,0.4)',
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="feed" aria-label="feed view">
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            
            {/* Filter Button */}
            <Tooltip title={activeFiltersCount > 0 ? "Show active filters" : "Filter topics"} arrow>
              <IconButton 
                onClick={() => setShowTopicFilter(!showTopicFilter)}
                size="small"
                sx={{ 
                  background: viewMode === 'feed' ? 'transparent' : (showTopicFilter 
                    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                    : 'rgba(255,255,255,0.8)'),
                  backdropFilter: viewMode === 'feed' ? 'none' : 'blur(10px)',
                  border: viewMode === 'feed' ? 'none' : '1px solid rgba(226,232,240,0.6)',
                  borderRadius: viewMode === 'feed' ? '50%' : '12px',
                  width: viewMode === 'feed' ? 36 : 48,
                  height: viewMode === 'feed' ? 36 : 48,
                  color: viewMode === 'feed' ? '#262626' : (showTopicFilter ? 'white' : 'rgba(71,85,105,0.8)'),
                  boxShadow: viewMode === 'feed' ? 'none' : (showTopicFilter 
                    ? '0 4px 12px rgba(99,102,241,0.3)' 
                    : '0 2px 8px rgba(0,0,0,0.04)'),
                  '&:hover': {
                    background: viewMode === 'feed' 
                      ? 'rgba(0,0,0,0.05)' 
                      : (showTopicFilter 
                        ? 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)'
                        : 'rgba(99,102,241,0.1)'),
                    borderColor: viewMode === 'feed' ? 'none' : 'rgba(99,102,241,0.3)',
                    transform: viewMode === 'feed' ? 'none' : 'translateY(-2px)',
                    boxShadow: viewMode === 'feed' ? 'none' : (showTopicFilter 
                      ? '0 6px 16px rgba(99,102,241,0.4)'
                      : '0 4px 12px rgba(99,102,241,0.15)'),
                    color: viewMode === 'feed' ? '#262626' : (showTopicFilter ? 'white' : 'rgba(99,102,241,0.8)')
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <Badge 
                  badgeContent={activeFiltersCount} 
                  sx={{
                    '& .MuiBadge-badge': {
                      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                      boxShadow: '0 2px 4px rgba(239,68,68,0.3)',
                      fontSize: '11px',
                      fontWeight: 600
                    }
                  }}
                  invisible={activeFiltersCount === 0}
                >
                  <FilterListIcon sx={{ fontSize: viewMode === 'feed' ? 18 : 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        {showTopicFilter && (
          <Box sx={{ 
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1200px',
            background: viewMode === 'feed' ? '#fff' : 'rgba(255,255,255,0.7)',
            backdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
            WebkitBackdropFilter: viewMode === 'feed' ? 'none' : 'blur(20px)',
            borderRadius: 0,
            p: { xs: 1.5, sm: viewMode === 'feed' ? 2 : 3 },
            border: viewMode === 'feed' ? '1px solid #efefef' : '1px solid rgba(226,232,240,0.6)',
            borderTop: '1px solid #efefef',
            borderLeft: 'none',
            borderRight: 'none',
            boxShadow: viewMode === 'feed' ? 'none' : '0 4px 16px rgba(0,0,0,0.06)',
            mb: 0,
            mx: viewMode === 'feed' ? 'auto' : { xs: '4px', sm: '10px' },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: { xs: 'none', sm: 'block' }
          }}>
            {isLoading ? (
              // Topic filter loading state
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 2 }, 
                minWidth: { xs: 'max-content', sm: 'auto' }
              }}>
                {[1, 2, 3, 4, 5].map((_, index) => (
                  <Skeleton 
                    key={index} 
                    variant="rectangular" 
                    width={120} 
                    height={32} 
                    sx={{ borderRadius: 4 }} 
                  />
                ))}
              </Box>
            ) : (
              <TopicFilter
                width="100%"
                onSelect={(topic) => {
                  setFilterBy(topic);
                  setSelectedTopics([topic]);
                  getRatesRequest(topic, [topic]);
                }}
                firstTopics={topTopics}
                onTopicsReady={handleTopicsReady}
              />
            )}
          </Box>
        )}
        {showTopicFilter && isMobile && !isLoading && (
          <TopicFilter
            width="100%"
            onSelect={(topic) => {
              setFilterBy(topic);
              setSelectedTopics([topic]);
              getRatesRequest(topic, [topic]);
            }}
            firstTopics={topTopics}
            onTopicsReady={handleTopicsReady}
          />
        )}
        </Box>
        
        <Box
          ref={containerRef}
          sx={{ 
            mt: viewMode === 'feed' ? 0 : { xs: 1, sm: 3, md: 4 },
            background: viewMode === 'feed' ? '#fafafa' : 'transparent',
            minHeight: viewMode === 'feed' ? '100vh' : 'auto',
            maxHeight: viewMode === 'feed' ? 'none' : { xs: '75vh', sm: '80vh' },
            overflowY: viewMode === 'feed' ? 'auto' : 'auto',
            p: viewMode === 'feed' ? 0 : { xs: 0.5, sm: 2, md: 3 },
            maxWidth: viewMode === 'feed' ? { xs: '100%', sm: '600px' } : '1200px',
            mx: 'auto',
            width: '100%',
            // Custom scrollbar - minimal for feed view
            '&::-webkit-scrollbar': {
              width: viewMode === 'feed' ? '0px' : '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '10px',
              '&:hover': {
                background: viewMode === 'feed' ? 'transparent' : 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
              }
            },
          }}
        >
          <Grid 
            container 
            spacing={viewMode === 'feed' ? 0 : { xs: 1.5, sm: 3 }}
            sx={{ 
              width: '100%',
              margin: 0,
              '& .MuiGrid-item': {
                padding: viewMode === 'feed' ? { xs: '4px 0', sm: '8px 0' } : undefined
              }
            }}
          >
        {isLoading ? (
          // Loading skeletons for cards
          [...Array(6)].map((_, index) => (
            <Grid 
              item 
              xs={viewMode === 'feed' ? 12 : 12} 
              sm={viewMode === 'feed' ? 12 : 6} 
              md={viewMode === 'feed' ? 12 : 4} 
              key={`skeleton-${index}`}
              sx={{ 
                display: 'flex',
                minHeight: viewMode === 'grid' ? '350px' : 'auto'
              }}
            >
              <Paper 
                sx={{ 
                  height: viewMode === 'feed' ? 'auto' : '330px', 
                  marginBottom: '10px', 
                  padding: '16px',
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
                <Skeleton variant="text" height={28} width="80%" sx={{ mb: 1 }} />
                <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                  <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                </Box>
              </Paper>
            </Grid>
          ))
        ) : (
          <>
            {loadError ? (
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: { xs: 8, sm: 12 },
                  px: { xs: 2, sm: 4 },
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(220,38,38,0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}>
                    <StarOutlineIcon sx={{
                      fontSize: { xs: 40, sm: 50 },
                      color: 'rgba(239,68,68,0.6)'
                    }} />
                  </Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 600,
                    color: 'rgba(30,41,59,0.9)',
                    mb: 1.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    Couldn't load reviews
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: 'rgba(71,85,105,0.7)',
                    maxWidth: '500px',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6,
                    mb: 3
                  }}>
                    Something went wrong while loading reviews. Please check your connection and tap retry.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => getRatesRequest(filterBy, selectedTopics)}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                      }
                    }}
                  >
                    Retry
                  </Button>
                </Box>
              </Grid>
            ) : data.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: { xs: 8, sm: 12 },
                  px: { xs: 2, sm: 4 },
                  textAlign: 'center'
                }}>
                  <Box sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.1) 0%, rgba(139,92,246,0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 3
                  }}>
                    <StarOutlineIcon sx={{
                      fontSize: { xs: 40, sm: 50 },
                      color: 'rgba(99,102,241,0.6)'
                    }} />
                  </Box>
                  <Typography variant="h5" sx={{
                    fontWeight: 600,
                    color: 'rgba(30,41,59,0.9)',
                    mb: 1.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {organizationMode ? 'No Organization Reviews Yet' : 'No Reviews Yet'}
                  </Typography>
                  <Typography variant="body1" sx={{
                    color: 'rgba(71,85,105,0.7)',
                    maxWidth: '500px',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.6
                  }}>
                    {organizationMode
                      ? 'There are no reviews in your organization yet. Start by creating your first review!'
                      : 'There are no reviews to display yet. Start exploring and rate your favorite items!'}
                  </Typography>
                </Box>
              </Grid>
            ) : (
              <>
                {data.map((m, index) => (
                  <Grid 
                    item 
                    xs={viewMode === 'feed' ? 12 : 12} 
                    sm={viewMode === 'feed' ? 12 : 6} 
                    md={viewMode === 'feed' ? 12 : 4} 
                    key={index}
                    sx={{ 
                      display: 'flex',
                      minHeight: viewMode === 'grid' ? '350px' : 'auto'
                    }}
                  >
                    <Box sx={{ 
                      height: viewMode === 'feed' ? '100%' : '100%',
                      maxWidth: viewMode === 'feed' ? '100%' : 'auto',
                      margin: viewMode === 'feed' ? '0 auto' : 'auto',
                      minHeight: viewMode === 'grid' ? { xs: '280px', sm: '330px' } : 'auto',
                      display: 'flex',
                      width: '100%',
                      marginBottom: viewMode === 'feed' ? { xs: 1, sm: 3 } : { xs: '8px', sm: '10px' },
                      padding: viewMode === 'feed' ? 0 : { xs: '4px', sm: '10px' },
                    }}>
                      {viewMode === 'grid' ? (
                        <HomeCardItem
                          isBookmark={true}
                          data={m}
                          applyMargin={true}
                          showRemove={true}
                          onRemove={(id) => {
                            setRemoveDialogOpen(true);
                            setRemovedRateId(id);
                          }}
                          onRateSuccessfully
                          isCommunity={true}
                        />
                      ) : (
                        <FeedCardItem
                          isBookmark={true}
                          data={m}
                          applyMargin={true}
                          showRemove={true}
                          onRemove={(id) => {
                            setRemoveDialogOpen(true);
                            setRemovedRateId(id);
                          }}
                          onRateSuccessfully
                          isCommunity={true}
                        />
                      )}
                    </Box>
                  </Grid>
                ))}
                {isLoadingMore && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </>
        )}
          </Grid>
        </Box>
      </Box>
      </Box>

      <ConfirmationDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={() => {
          deleteRate();
          setRemoveDialogOpen(false);
        }}
        title={"Delete rate"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={"Are you sure you want to delete this rate?"}
      />

      {/* Mobile Filter Modal - Outside filter component */}
      {isMobile && (
        <MobileFilterView
          isOpen={showMobileTopicModal}
          onClose={() => {
            setShowMobileTopicModal(false);
            setShowTopicFilter(false);
          }}
          items={mobileTopicsData}
          selectedItems={selectedTopics}
          onApply={handleMobileTopicApply}
          title="Filter by Topics"
          type="topics"
        />
      )}
    </>
  );
};

export default CommunityRates;
